import { Injectable } from '@angular/core';
import {
	DOCUMENT_CHECK_ID,
	IOcr,
	IOcrConf,
	IOcrDocumentStatus,
	IOcrHistory,
	SEG_HISTORY_INTERVAL,
} from '../../models/ocr';
import { TranslateService } from '@ngx-translate/core';
import { IAlert } from 'src/app/core/models/alert.models';
import { MODEL_DOCUMENT_CATEGORY, TestStatus } from '../../models/user-identification';
import { PositionDocumentService } from './position-document.service';
import { ObjectDetectorResult } from '@mediapipe/tasks-vision';
import { CameraService } from 'src/app/shared/services/camera.service';
import { IOcrAllowedDocs } from '../../models/user-identification';
import { isFirstSideScan, resolveFirstScanOnWarningKey } from '../../utils/ocr-allowed-dni-label.util';

@Injectable({
	providedIn: 'root',
})
export class AudioHistoryService {
	public dataHistoryArray: IOcrHistory[] = [];

	constructor(
		private trans: TranslateService,
		private positionDocumentService: PositionDocumentService,
		private cameraService: CameraService,
	) {}

	public getStateOcr(
		ocrConf: IOcrConf,
		results: ObjectDetectorResult,
		allowedDocs?: IOcrAllowedDocs | null,
	): IOcrDocumentStatus[] {
		return [
			{
				id: 'hasNoDocument',
				repeatTime: 12,
				promptConfig: this.buildHasNoDocumentPrompts(ocrConf, allowedDocs),
				conditions: () => this.getDataHistoryLastSecs(10)?.every((h: IOcrHistory) => !h.documentDetection),
			},
			{
				id: 'isTooFar',
				repeatTime: 12,
				promptConfig: [
					{
						audio: 'OCR.AUDIO.IS_TOO_FAR_1',
						centerText: this.trans.instant('OCR.GENERAL.IS_TOO_FAR'),
						alert: this.getEmptyAlert(),
					},
					{
						audio: 'OCR.AUDIO.IS_TOO_FAR_2',
						centerText: this.trans.instant('OCR.GENERAL.IS_TOO_FAR'),
						alert: this.getEmptyAlert(),
					},
					{
						audio: 'OCR.AUDIO.KEEP_STEADY',
						centerText: this.trans.instant('OCR.GENERAL.IS_TOO_FAR'),
						alert: this.getEmptyAlert(),
					},
				],
				conditions: () =>
					this.getDataHistoryLastSecs(5)?.every(
						(h: IOcrHistory) => !h.documentCentered && h.documentDetection && !h.animation && !h.isTooClose,
					),
			},
			{
				id: 'isTooClose',
				repeatTime: 12,
				promptConfig: [
					{
						audio: 'OCR.AUDIO.IS_TOO_CLOSE_1',
						centerText: this.trans.instant('OCR.GENERAL.IS_TOO_CLOSE'),
						alert: this.getEmptyAlert(),
					},
					{
						audio: 'OCR.AUDIO.IS_TOO_CLOSE_2',
						centerText: this.trans.instant('OCR.GENERAL.IS_TOO_CLOSE'),
						alert: this.getEmptyAlert(),
					},
					{
						audio: 'OCR.AUDIO.KEEP_STEADY',
						centerText: this.trans.instant('OCR.GENERAL.IS_TOO_CLOSE'),
						alert: this.getEmptyAlert(),
					},
				],
				conditions: () =>
					this.getDataHistoryLastSecs(5)?.every(
						(h: IOcrHistory) => !h.documentCentered && h.documentDetection && !h.animation && h.isTooClose,
					),
			},
			{
				id: 'isBlurry',
				repeatTime: 12,
				promptConfig: [
					{
						audio: 'OCR.AUDIO.IS_BLURRY_3',
						centerText: this.trans.instant('OCR.GENERAL.FOCUS_IMAGE'),
						alert: this.getEmptyAlert(),
					},
					{
						audio:
							this.cameraService.allCamerasValue.length > 2
								? 'OCR.AUDIO.IS_BLURRY_WITH_MULTIPLE_CAMERAS'
								: 'OCR.AUDIO.IS_BLURRY_1',
						centerText: this.trans.instant('OCR.GENERAL.FOCUS_IMAGE'),
						alert:
							this.cameraService.allCamerasValue.length > 2
								? {
										label: this.trans.instant('OCR.GENERAL.AVOID_GLARE_OR_CHANGE_CAMERA'),
										status: 'default',
										icon: 'icon-info',
									}
								: this.createAvoidGlareAlert(),
					},
					{
						audio: 'OCR.AUDIO.KEEP_STEADY',
						centerText: this.trans.instant('OCR.GENERAL.FOCUS_IMAGE'),
						alert: this.createAvoidGlareAlert(),
					},
				],
				conditions: () =>
					this.getDataHistoryLastSecs(2.5)?.every(
						(h: IOcrHistory) => !h.isSharpValid && h.documentCentered && h.documentDetection,
					),
			},
			{
				id: 'isOverexposed',
				repeatTime: 12,
				promptConfig: [
					{
						audio: 'OCR.AUDIO.IS_OVEREXPOSED_1',
						centerText: '',
						alert: this.createOverexposedAlert(),
						keepPrevCenterText: true,
					},
					{
						audio: 'OCR.AUDIO.IS_OVEREXPOSED_2',
						centerText: this.trans.instant('OCR.GENERAL.FOCUS_IMAGE'),
						alert: this.createOverexposedAlert(),
					},
				],
				conditions: () =>
					this.getDataHistoryLastSecs(2.5)?.every(
						(h: IOcrHistory) => !h.isBrightnessValid && h.documentCentered && h.documentDetection,
					),
			},
		];
	}

	/**
	 * Obtiene un resumen del historial (últimos N segundos)
	 * @param lastSeconds Número de últimos segundos a obtener
	 * @returns Array con los últimos N snapshots
	 */
	public getDataHistoryLastSecs(lastSeconds: number): any[] {
		return lastSeconds / SEG_HISTORY_INTERVAL > this.dataHistoryArray.length
			? null
			: this.dataHistoryArray.slice(-lastSeconds / SEG_HISTORY_INTERVAL);
	}

	/**
	 * Limpia el historial de datos
	 */
	public clearDataHistory(): void {
		this.dataHistoryArray = [];
	}

	public addDataHistory(data: IOcrHistory): void {
		this.dataHistoryArray.push(data);

		// Limitar el tamaño del array para no consumir demasiada memoria
		if (this.dataHistoryArray.length >= 500) {
			this.dataHistoryArray.shift();
		}
	}

	/**
	 * Prompts cuando no hay documento detectado (3 fases en 1ª y 2ª cara):
	 * ONINIT → ONWARNING / ALIGN_DOCUMENT → IS_BLURRY_1 + AVOID_GLARE.
	 */
	private buildHasNoDocumentPrompts(
		ocrConf: IOcrConf,
		allowedDocs?: IOcrAllowedDocs | null,
	): IOcrDocumentStatus['promptConfig'] {
		const warningKey = this.getLabelWarningSide(ocrConf, allowedDocs);
		const initPrompt = {
			audio: this.getInitDocumentMsg(ocrConf),
			centerText: '',
			alert: this.getEmptyAlert(),
			keepPrevCenterText: true,
		};
		const alignPrompt = {
			audio: warningKey,
			centerText: isFirstSideScan(ocrConf)
				? this.trans.instant(warningKey)
				: this.trans.instant('OCR.GENERAL.ALIGN_DOCUMENT'),
			alert: this.getEmptyAlert(),
		};

		// NIF-610 limitó a 2 prompts en 2ª cara (sin fase de brillos).
		// Al día siguiente producto pidió de nuevo las 3 fases (incl. brillos).
		// Se deja comentado por si el criterio vuelve a cambiar.
		// if (!isFirstSideScan(ocrConf)) {
		// 	return [
		// 		initPrompt,
		// 		{
		// 			...alignPrompt,
		// 			centerText: this.trans.instant('OCR.GENERAL.ALIGN_DOCUMENT'),
		// 		},
		// 	];
		// }

		return [
			initPrompt,
			alignPrompt,
			{
				audio: 'OCR.AUDIO.IS_BLURRY_1',
				centerText: '',
				alert: this.createAvoidGlareAlert(),
				keepPrevCenterText: true,
			},
		];
	}

	/**
	 * Devuelve un objeto de alerta predeterminado con una etiqueta vacía y estado 'default'.
	 * @returns {IAlert} El objeto de alerta por defecto.
	 */
	public getEmptyAlert(): IAlert {
		return { label: '', status: 'default' };
	}

	/**
	 * Crea una alerta informativa para advertir al usuario sobre el resplandor, desenfoque o presencia de dedos en la imagen.
	 *
	 * @returns {IAlert} Objeto de alerta con etiqueta, estado e icono configurados.
	 */
	private createAvoidGlareAlert(): IAlert {
		return {
			label: this.trans.instant('OCR.GENERAL.AVOID_GLARE_BLUR_FINGERS'),
			status: 'default',
			icon: 'icon-info',
		};
	}

	/**
	 * Crea una alerta informativa para advertir al usuario sobre exceso de brillo en la imagen.
	 *
	 * @returns {IAlert} Objeto de alerta con etiqueta, estado e icono configurados.
	 */
	private createOverexposedAlert(): IAlert {
		return {
			label: this.trans.instant('OCR.GENERAL.IS_OVEREXPOSED'),
			status: 'default',
			icon: 'icon-info',
		};
	}

	/**
	 * Crea una alerta informativa para advertir al usuario que debe rotar el documento.
	 * Utiliza la traducción correspondiente para el mensaje de la alerta según el lado que se está analizando.
	 *
	 * @returns {IAlert} Objeto de alerta con etiqueta, estado e icono configurados.
	 */
	public createIncorrectSideAlert(ocrConf: IOcrConf, results: ObjectDetectorResult): IAlert {
		// Obtener la categoría detectada del documento
		const category = this.positionDocumentService.getDocumentDetection(results?.detections);
		const isPassport = category?.name === MODEL_DOCUMENT_CATEGORY.PASSPORT;

		let labelKey = this.getLabelWarningSide(ocrConf, undefined);

		// Para pasaporte, usar mensaje específico sobre brillos
		if (isPassport) labelKey = 'OCR.BACK.INSTRUCTIONS.TEXT.ONWARNING_PASSPORT';

		const label = this.trans.instant(labelKey);

		return {
			label,
			status: 'default',
			icon: 'icon-info',
		};
	}

	public getInitDocumentMsg(ocrConf: IOcrConf): string {
		const found = ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		let message: string = '';
		if (found) {
			if (found.status === TestStatus.OnInit || found.status === TestStatus.OnWarning) {
				message = found.messages[TestStatus.OnInit];
			}
		}
		return message;
	}

	private getLabelWarningSide(ocrConf: IOcrConf, allowedDocs?: IOcrAllowedDocs | null): string {
		const documentTest = ocrConf?.document?.find((ocr: IOcr) => !ocr.complete);
		const onInit = documentTest?.messages?.onInit;

		// side = front | back (promptRequiredSide): pedir esa cara del documento, sin pasaporte.
		if (onInit === 'OCR.FRONT.INSTRUCTIONS.TEXT.ONINIT') {
			return 'OCR.FRONT.INSTRUCTIONS.TEXT.ONWARNING';
		}
		if (onInit === 'OCR.BACK.INSTRUCTIONS.TEXT.ONINIT') {
			return 'OCR.BACK.INSTRUCTIONS.TEXT.ONWARNING';
		}

		// side = null (1ª captura genérica): documento o pasaporte según allowed_docs.
		if (isFirstSideScan(ocrConf)) {
			return resolveFirstScanOnWarningKey(allowedDocs);
		}

		const isFrontSide = documentTest?.documentTestId === DOCUMENT_CHECK_ID.FRONT;

		// Usar el mensaje apropiado según el lado y tipo de documento
		return isFrontSide ? 'OCR.FRONT.INSTRUCTIONS.TEXT.ONWARNING' : 'OCR.BACK.INSTRUCTIONS.TEXT.ONWARNING';
	}
}
